/**
 * /suprimentos/fornecedores — CRUD simples.
 */
import React, { useCallback, useEffect, useState } from "react";
import Checkbox from "../../components/Checkbox";
import { Carregando, Vazio } from "./_shared/Estados";
import { FiAlertCircle, FiEdit2, FiPlus, FiRefreshCw, FiTag, FiTrash2 } from "react-icons/fi";

import EntityHeader from "../../components/EntityHeader";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import {
  atualizarFornecedor,
  criarFornecedor,
  deletarFornecedor,
  listarFornecedores,
} from "../../services/compras";
import { unidadeDefault } from "../../services/unidades";
import ProdutoFornecedoresLista from "./_shared/ProdutoFornecedoresLista";

import {
  Card,
  ErrorMsg,
  Field,
  Form,
  FormFooter,
  FormGrid,
  IconButton,
  Input,
  Pill,
  PrimaryButton,
  SearchInput,
  SecondaryButton,
  Select,
  Table,
  TableWrapper,
  Textarea,
  TruncCell,
} from "./styles";

function Fornecedores() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const [unidade, setUnidade] = useState(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState({ busca: "", ativo: "" });
  const [editando, setEditando] = useState(null);
  // Bloco 6 (12/09): produtos e precos deste fornecedor.
  const [precosDe, setPrecosDe] = useState(null);

  const podeGerenciar = isMaster || hasAllPermissions(["compras:gerenciar_fornecedor"]);

  useEffect(() => {
    unidadeDefault(request).then(setUnidade);
  }, [request]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageSize: 200 };
      if (filtros.busca) params.busca = filtros.busca;
      if (filtros.ativo) params.ativo = filtros.ativo === "true";
      const payload = await listarFornecedores(request, params);
      const items = Array.isArray(payload) ? payload : payload?.results || [];
      setFornecedores(items);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  }, [request, filtros.busca, filtros.ativo]);

  useEffect(() => { carregar(); }, [carregar]);

  const handleSalvar = async (e) => {
    e.preventDefault();
    setErro(null);
    try {
      const dados = {
        nome: editando.nome,
        documento: editando.documento || "",
        email: editando.email || "",
        telefone: editando.telefone || "",
        contato: editando.contato || "",
        observacao: editando.observacao || "",
        ativo: editando.ativo !== false,
        business_unit: editando.business_unit || unidade?.id,
      };
      if (editando.id) {
        await atualizarFornecedor(request, editando.id, dados);
      } else {
        await criarFornecedor(request, dados);
      }
      setEditando(null);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao salvar.");
    }
  };

  const handleDeletar = async (f) => {
    if (!window.confirm(`Apagar fornecedor "${f.nome}"?`)) return;
    try {
      await deletarFornecedor(request, f.id);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao apagar (provavelmente tem OCs).");
    }
  };

  return (
    <>
      <EntityHeader
        title="Fornecedores"
        subtitle={`${fornecedores.length} cadastrado${fornecedores.length !== 1 ? "s" : ""}`}
        primaryAction={
          podeGerenciar
            ? {
                label: "Novo fornecedor",
                icon: <FiPlus size={14} />,
                onClick: () => setEditando({
                  nome: "", documento: "", email: "", telefone: "",
                  contato: "", observacao: "", ativo: true,
                }),
              }
            : undefined
        }
        actions={[
          { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
        ]}
      />

      <Card>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <SearchInput
            type="search"
            placeholder="Buscar por nome/documento..."
            value={filtros.busca}
            onChange={(e) => setFiltros((p) => ({ ...p, busca: e.target.value }))}
          />
          <Select
            value={filtros.ativo}
            onChange={(e) => setFiltros((p) => ({ ...p, ativo: e.target.value }))}
          >
            <option value="">Ativos + inativos</option>
            <option value="true">Só ativos</option>
            <option value="false">Só inativos</option>
          </Select>
        </div>

        {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}

        {loading ? (
          <Carregando />
        ) : fornecedores.length === 0 ? (
          <Vazio titulo="Nenhum fornecedor cadastrado" />
        ) : (
          <TableWrapper>
            <Table>
              <colgroup>
                <col style={{ width: "28%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Documento</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {fornecedores.map((f) => (
                  <tr key={f.id}>
                    <td title={f.nome}><TruncCell>{f.nome}</TruncCell></td>
                    <td>{f.documento || "—"}</td>
                    <td title={f.email}><TruncCell>{f.email || "—"}</TruncCell></td>
                    <td>{f.telefone || "—"}</td>
                    <td>
                      <Pill $tone={f.ativo ? "success" : "neutral"}>
                        {f.ativo ? "Ativo" : "Inativo"}
                      </Pill>
                    </td>
                    <td>
                      {(
                        <div style={{ display: "flex", gap: 4 }}>
                          <IconButton type="button" onClick={() => setPrecosDe(f)} title="Produtos e preços">
                            <FiTag size={11} />
                          </IconButton>
                          {podeGerenciar && (<>
                          <IconButton type="button" onClick={() => setEditando(f)} title="Editar">
                            <FiEdit2 size={11} />
                          </IconButton>
                          <IconButton type="button" onClick={() => handleDeletar(f)} title="Apagar">
                            <FiTrash2 size={11} />
                          </IconButton>
                          </>)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <SlideOverPanel
        open={Boolean(precosDe)}
        onClose={() => setPrecosDe(null)}
        title={precosDe ? `Produtos e preços · ${precosDe.nome}` : ""}
        width="min(94vw, 680px)"
      >
        {precosDe ? <ProdutoFornecedoresLista fornecedorId={precosDe.id} /> : null}
      </SlideOverPanel>

      <SlideOverPanel
        open={Boolean(editando)}
        onClose={() => setEditando(null)}
        title={editando?.id ? "Editar fornecedor" : "Novo fornecedor"}
        width="min(90vw, 480px)"
      >
        {editando && (
          <Form onSubmit={handleSalvar}>
            <Field>
              Nome *
              <Input
                type="text"
                value={editando.nome}
                onChange={(e) => setEditando((p) => ({ ...p, nome: e.target.value }))}
                maxLength={200}
                required
                autoFocus
              />
            </Field>
            <FormGrid>
              <Field>
                CNPJ/CPF
                <Input
                  type="text"
                  value={editando.documento}
                  onChange={(e) => setEditando((p) => ({ ...p, documento: e.target.value }))}
                  maxLength={18}
                />
              </Field>
              <Field>
                Telefone
                <Input
                  type="text"
                  value={editando.telefone}
                  onChange={(e) => setEditando((p) => ({ ...p, telefone: e.target.value }))}
                  maxLength={30}
                />
              </Field>
            </FormGrid>
            <FormGrid>
              <Field>
                Email
                <Input
                  type="email"
                  value={editando.email}
                  onChange={(e) => setEditando((p) => ({ ...p, email: e.target.value }))}
                />
              </Field>
              <Field>
                Contato
                <Input
                  type="text"
                  value={editando.contato}
                  onChange={(e) => setEditando((p) => ({ ...p, contato: e.target.value }))}
                  maxLength={120}
                />
              </Field>
            </FormGrid>
            <Field>
              Observação
              <Textarea
                value={editando.observacao}
                onChange={(e) => setEditando((p) => ({ ...p, observacao: e.target.value }))}
                rows={3}
              />
            </Field>
            {editando.id && (
              <Field>
                <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <Checkbox
                    
                    checked={editando.ativo !== false}
                    onChange={(e) => setEditando((p) => ({ ...p, ativo: e.target.checked }))}
                  />
                  Ativo
                </label>
              </Field>
            )}
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setEditando(null)}>Cancelar</SecondaryButton>
              <PrimaryButton type="submit">{editando.id ? "Salvar" : "Criar"}</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>
    </>
  );
}

export default Fornecedores;
